import { Card, CardHeader, CardBody } from '@/shared/components/ui/Card';
import { Table, type Column } from '@/shared/components/ui/Table';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { formatDuration } from '../constants';
import { useCallCenter } from '../hooks/useCallCenter';
import type { CallCenterEmployee } from '../types';

export function TeamMonitor({ enabled }: { enabled: boolean }) {
  const { data = [], isLoading, isError } = useCallCenter(enabled);

  const breakCount = (e: CallCenterEmployee) =>
    Object.values(e.breaks || {}).reduce((n, b) => n + (b.count || 0), 0);

  const columns: Column<CallCenterEmployee>[] = [
    { key: 'name', header: 'Agent', render: (e) => <span className="font-medium text-on-surface">{e.display_name}</span> },
    { key: 'department', header: 'Dept', render: (e) => e.department || '—' },
    { key: 'calls_today', header: 'Calls', align: 'right', render: (e) => e.calls_today },
    { key: 'talked', header: 'Connected', align: 'right', render: (e) => e.talked },
    { key: 'demos_done', header: 'Demos', align: 'right', render: (e) => e.demos_done },
    { key: 'duration_sec', header: 'Talk Time', align: 'right', render: (e) => formatDuration(e.duration_sec) },
    { key: 'breaks', header: 'Breaks', align: 'right', render: (e) => breakCount(e) },
    { key: 'check_in', header: 'In', render: (e) => e.time_today?.check_in || '—' },
    { key: 'check_out', header: 'Out', render: (e) => e.time_today?.check_out || '—' },
  ];

  return (
    <Card>
      <CardHeader title="Team Monitoring — Today" />
      <CardBody>
        {isLoading ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : isError ? (
          <EmptyState title="Failed to load team data" description="Check your connection or try refreshing." />
        ) : data.length === 0 ? (
          <EmptyState title="No agent activity today" description="Call and break activity will appear here." />
        ) : (
          <Table columns={columns} data={data} rowKey={(e) => e.user_id} />
        )}
      </CardBody>
    </Card>
  );
}
